/**
 * @file src/hooks/LanguageOrchestrator.js
 */
import { dictionary } from '../api/dictionary';

class LanguageOrchestrator {
  static LOCAL_ONLY_NAMESPACES = new Set(['footer']);

  static SHADOW_DICTIONARY = {
    'branding': {
      TAGLINE: 'Moksy AI Platform',
      NAME: 'Moksy'
    },
    'auth-login': {
      PAGE: { TITLE: 'Sign In' },
      FIELDS: {
        EMAIL_LABEL: 'Email Address',
        EMAIL_PLACEHOLDER: 'you@example.com',
        PASSWORD_LABEL: 'Password',
        PASSWORD_PLACEHOLDER: '••••••••'
      },
      BUTTONS: { AUTHENTICATE: 'Sign In' },
      MESSAGES: { INVALID_CREDENTIALS: 'Invalid email or password. Please try again.' },
      FOOTER: { SECURITY_NOTICE: 'Your connection is secure' }
    },
    'auth-register': {
      PAGE: { TITLE: 'Create Account' },
      FIELDS: {
        USERNAME_LABEL: 'Username',
        USERNAME_PLACEHOLDER: 'Choose a username',
        EMAIL_LABEL: 'Email Address',
        EMAIL_PLACEHOLDER: 'you@example.com',
        PASSWORD_LABEL: 'Password',
        PASSWORD_PLACEHOLDER: 'Minimum 8 characters'
      },
      BUTTONS: { REGISTER: 'Create Account' },
      MESSAGES: { PROVISIONING_FAILED: 'Registration failed. Please try again.' },
      FOOTER: { LOGIN_LINK: 'Already have an account? Sign in' }
    },
    'dashboard': {
      LIMIT_CARD: {
        TITLE: 'You have reached your limit',
        SUBTITLE: 'Sign up to get more credit',
        UPGRADE_SUBTITLE: 'Upgrade your subscription to get more credit',
        GUEST_ACTION: 'Sign Up',
        USER_ACTION: 'Upgrade Plan'
      }
    },
    'profile': {
      PAGE: { TITLE: 'My Profile', SUBTITLE: 'Manage your account details' }
    },
    'infra': {
      PAGE: { TITLE: 'Infrastructure Overview', SUBTITLE: 'Monitor the health and status of platform services.', STANDBY_LABEL: 'All systems operational' },
      BUTTONS: { EMERGENCY_SHIELD: 'Emergency Shutdown' }
    },
    'asset-hub': {
      PAGE: { TITLE: 'Resource Vault', SUBTITLE: 'Manage platform content, language files, and configuration assets.', ADD_BUTTON: 'Add Resource' },
      MODAL: { TITLE: 'Add Resource', SUBTITLE: 'Create a new dictionary resource', SAVE: 'Create', CANCEL: 'Cancel' },
      EDITOR: { SAVE_BUTTON: 'Save', SYNC_STATUS: 'Saving...', INVALID_JSON: 'Invalid JSON — please fix the formatting before saving.' }
    },
    'user-manager': {
      PAGE: { TITLE: 'User Management', SUBTITLE: 'Manage platform users and their roles', ADD_BUTTON: 'Add User' },
      TABLE: { COL_EMAIL: 'Email Address', COL_ROLE: 'Role', COL_CREATED: 'Joined', COL_ACTIONS: 'Actions' },
      MODAL: { TITLE_CREATE: 'Create User', TITLE_EDIT: 'Edit User', CONFIRM_DELETE: 'Are you sure you want to delete this user?', SAVE: 'Save', CANCEL: 'Cancel' },
      FIELDS: { USERNAME: 'Username', EMAIL: 'Email Address', PASSWORD: 'Password', ROLE: 'Role' },
      ACTIONS: { MODIFY: 'Edit', PURGE: 'Delete' }
    },
    'footer': {
      links: []
    }
  };

  static #loaded = new Map();
  static #inflight = new Map();

  /** Fetch a namespace once; later callers share the result or the request. */
  static load(namespace) {
    if (this.#loaded.has(namespace)) return Promise.resolve(this.#loaded.get(namespace));
    if (this.#inflight.has(namespace)) return this.#inflight.get(namespace);
    const p = dictionary.getPublic(`i18n/en/${namespace}`)
      .then((asset) => { this.#loaded.set(namespace, asset ?? null); return asset; })
      .catch(() => null)
      .finally(() => { this.#inflight.delete(namespace); });
    this.#inflight.set(namespace, p);
    return p;
  }

  /** Tests and admin edits: forget what was loaded so the next ask refetches. */
  static forget(namespace) {
    if (namespace == null) { this.#loaded.clear(); this.#inflight.clear(); return; }
    this.#loaded.delete(namespace); this.#inflight.delete(namespace);
  }

  static async syncContent(namespace, setContent, currentContent, refs) {
    if (this.LOCAL_ONLY_NAMESPACES.has(namespace)) {
      if (refs.isMounted) {
        const fallback = this.SHADOW_DICTIONARY[namespace] || { PAGE: {} };
        setContent(fallback);
      }
      return;
    }
    // One request per namespace per page, shared by every component that
    // asks. Eleven components call useLanguage('branding') and each one
    // fetched on mount, so a Briefing page with re-renders sent the same
    // bundle request hundreds of times and the gateway answered 429 — to the
    // branding text, and to whatever else the person did in that window.
    const asset = await LanguageOrchestrator.load(namespace);
    
    if (refs.isMounted) {
      if (asset && typeof asset === 'object' && Object.keys(asset).length > 0) {
        setContent(asset);
      } else {
        // Silent fallback - no logging required for standard operation
        const fallback = this.SHADOW_DICTIONARY[namespace] || { PAGE: {} };
        setContent(fallback);
      }
    }
  }

  static getInitialState(namespace) {
    if (this.LOCAL_ONLY_NAMESPACES.has(namespace)) {
      return this.SHADOW_DICTIONARY[namespace] || { PAGE: {} };
    }
    return { PAGE: {} };
  }
}

export { LanguageOrchestrator };
